// Imports
import { CellContext, createColumnHelper } from "@tanstack/react-table";
import { t } from "i18next";
import { MdSensors, MdStackedBarChart } from "react-icons/md";
import { formatDate } from "util/datelib";
import CircleButton from "widgets/controls/buttons/circle-button";

// Define the structure of the JdTableModel
export class RawsPageTableModel {
  _id: string;
  snr: string;
  created: string;
  version: string;
  ref: string;
  via: string;
  recovered: boolean;
  inserted: string;
  state: {
    sensors: [];
    status: [];
    events: [];
  };
  actions: string;
}

// Helper function to format elapsed time
const formatElapsedTime = (milliseconds: number): string => {
  if (milliseconds === 0) return "0s";

  const seconds = Math.floor(milliseconds / 1000);
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);

  if (days > 0) {
    const remainingHours = hours % 24;
    return remainingHours > 0 ? `${days}d ${remainingHours}h` : `${days}d`;
  }
  if (hours > 0) {
    const remainingMinutes = minutes % 60;
    return remainingMinutes > 0
      ? `${hours}h ${remainingMinutes}m`
      : `${hours}h`;
  }
  if (minutes > 0) {
    const remainingSeconds = seconds % 60;
    return remainingSeconds > 0
      ? `${minutes}m ${remainingSeconds}s`
      : `${minutes}m`;
  }
  return `${seconds}s`;
};

const defineRawsTablecolumns = (
  onSensorsClick?: (info: CellContext<RawsPageTableModel, any>) => void,
  onStateClick?: (info: CellContext<RawsPageTableModel, any>) => void
) => {
  // create a column helper for the JdTableModel
  const columnHelper = createColumnHelper<RawsPageTableModel>();

  // Define the columns for the JdTable
  const rawsPageTablecolumns = [
    // Created timestamp column
    columnHelper.accessor("created", {
      enableSorting: false,
      header: () => <span className="">{t("table.header.raw.created")}</span>,
      cell: (info) => (
        <span className="">{formatDate(info.getValue(), "lastcomms")}</span>
      ),
      meta: { rowClick: false },
    }),

    // Elapsed time column
    columnHelper.display({
      id: "elapsed",
      enableSorting: false,
      header: () => <span className="">{t("table.header.raw.elapsed")}</span>,
      cell: (info) => {
        const currentRow = info.row.index;
        const rows = info.table.getRowModel().rows;

        if (currentRow === 0) {
          return <span className="">0s</span>;
        }

        const currentTime = new Date(info.row.original.created).getTime();
        const previousTime = new Date(
          rows[currentRow - 1].original.created
        ).getTime();
        const elapsed = Math.abs(currentTime - previousTime);

        return <span className="">{formatElapsedTime(elapsed)}</span>;
      },
      meta: { rowClick: false },
    }),

    // Status column
    columnHelper.accessor("version", {
      enableSorting: false,
      header: () => <span className="">{t("table.header.raw.version")}</span>,
      cell: (info) => <span className="">{info.getValue()}</span>,
      meta: { rowClick: false },
    }),

    // Actions column
    columnHelper.accessor("actions", {
      enableSorting: false,
      header: () => <span className="">{t("table.header.actions")}</span>,
      cell: (info) => {
        return (
          <div className="flex flex-row">
            <CircleButton
              icon={MdSensors}
              size="sm"
              variant="ghost"
              onClick={() => {
                onSensorsClick?.(info);
              }}
              tooltip={t("tooltip.sensors")}
              tooltipPlacement="bottom"
            />

            <CircleButton
              icon={MdStackedBarChart}
              size="sm"
              variant="ghost"
              onClick={() => {
                onStateClick?.(info);
              }}
              tooltip={t("tooltip.state")}
              tooltipPlacement="bottom"
            />
          </div>
        );
      },
    }),
  ];

  return rawsPageTablecolumns;
};

export default defineRawsTablecolumns;
